import { Button } from "./button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./card";
import { Zap, ArrowRight, CheckCircle2 } from "lucide-react";

interface SubscriptionPaywallProps {
  status:
    | "active"
    | "canceled"
    | "past_due"
    | "unpaid"
    | "incomplete"
    | "incomplete_expired"
    | "paused";
  onManagePayment: () => void;
  planName?: string;
  monthlyCredits?: number;
}

export function SubscriptionPaywall({
  status,
  onManagePayment,
  planName = "your plan",
  monthlyCredits = 0,
}: SubscriptionPaywallProps) {
  const getMessage = () => {
    switch (status) {
      case "past_due":
        return {
          title: "Payment Overdue",
          description:
            "Your recent payment was unsuccessful. Update your payment method to continue using our services.",
          action: "Update Payment Method",
          urgency: "medium",
        };
      case "unpaid":
        return {
          title: "Subscription Suspended",
          description:
            "Your subscription has been suspended due to payment issues. Reactivate now to regain access.",
          action: "Reactivate Subscription",
          urgency: "high",
        };
      case "incomplete":
        return {
          title: "Complete Your Subscription",
          description:
            "Your subscription setup is pending. Complete the payment to activate your account.",
          action: "Complete Setup",
          urgency: "low",
        };
      case "incomplete_expired":
        return {
          title: "Payment Failed",
          description:
            "The initial payment for your subscription failed. Please try again with a different payment method.",
          action: "Try Again",
          urgency: "high",
        };
      default:
        return {
          title: "Subscription Required",
          description:
            "Please update your subscription to continue using our services.",
          action: "Update Subscription",
          urgency: "medium",
        };
    }
  };

  const message = getMessage();
  const isHighUrgency = message.urgency === "high";

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-lg">
        <CardHeader>
          <div
            className={`inline-flex items-center justify-center rounded-full p-2 ${
              isHighUrgency ? "bg-red-100" : "bg-yellow-100"
            } mb-4`}
          >
            <Zap
              className={`h-6 w-6 ${
                isHighUrgency ? "text-red-600" : "text-yellow-600"
              }`}
            />
          </div>
          <CardTitle className="text-2xl">{message.title}</CardTitle>
          <p className="text-muted-foreground mt-2">{message.description}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <h3 className="font-medium">Why renew {planName}?</h3>
            <div className="grid gap-3">
              {monthlyCredits > 0 && (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {monthlyCredits.toLocaleString()} monthly credits
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Continue processing your leads efficiently
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Uninterrupted Service</p>
                  <p className="text-sm text-muted-foreground">
                    Keep your automation workflows running smoothly
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Save Your Settings</p>
                  <p className="text-sm text-muted-foreground">
                    Retain all your customized configurations and integrations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button className="w-full" size="lg" onClick={onManagePayment}>
            {message.action}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Need help? Contact our support team for assistance
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
